import androidx.compose.runtime.CompositionLocalProvider
import com.varabyte.kobweb.browser.api
import com.varabyte.kobweb.core.AppGlobalsLocal
import com.varabyte.kobweb.navigation.RoutePrefix
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.silk.defer.renderWithDeferred
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.dom.hasClass
import kotlinx.dom.removeClass
import org.jetbrains.compose.web.renderComposable
import org.w3c.dom.EventSource
import org.w3c.dom.EventSourceInit
import org.w3c.dom.MessageEvent
import org.w3c.dom.`get`

private fun forceReloadNow() {
    window.stop()
    window.location.reload()
}

private fun handleServerStatusEvents() {
    val status = document.getElementById("status")!!
    var lastVersion: Int? = null
    var shouldReload = false

    val warningIcon = status.children[0]!!
    val spinnerIcon = status.children[1]!!
    val statusText = status.children[2]!!

    status.addEventListener("transitionend", {
        if (status.hasClass("fade-out")) {
            status.removeClass("fade-out")
            if (shouldReload) {
                forceReloadNow()
            }
        }
    })

    val eventSource = EventSource("/api/kobweb-status", EventSourceInit(true))
    eventSource.addEventListener("version", { evt ->
        val version = (evt as MessageEvent).data.toString().toInt()
        if (lastVersion == null) {
            lastVersion = version
        }
        if (lastVersion != version) {
            lastVersion = version
            if (status.className.isNotEmpty()) {
                shouldReload = true
            } else {
                // Not sure if we can get here but if we can't rely on the status transition
                // to reload we should do it ourselves.
                forceReloadNow()
            }
        }
    })

    eventSource.addEventListener("status", { evt ->
        val values: dynamic = JSON.parse<Any>((evt as MessageEvent).data.toString())
        val text = values.text as String
        val isError = (values.isError as String).toBoolean()
        if (text.isNotBlank()) {
            warningIcon.className = if (isError) "visible" else "hidden"
            spinnerIcon.className = if (isError) "hidden" else "visible"
            statusText.innerHTML = "<i>$text</i>"
            status.className = "fade-in"
        } else {
            if (status.className == "fade-in") {
                status.className = "fade-out"
            }
        }
    })

    eventSource.onerror = { eventSource.close() }
}

public fun main() {
    handleServerStatusEvents()

    window.api.logOnError = true

    RoutePrefix.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.register("/") { ru.cleardocs.lkweb.pages.HomePage() }
        ctx.router.register("/auth") { ru.cleardocs.lkweb.pages.AuthPage() }
        ctx.router.register("/chat") { ru.cleardocs.lkweb.pages.ChatPage() }
        ctx.router.register("/demo/{...path?}") { ru.cleardocs.lkweb.pages.demo.DemoRouterPage() }
        ctx.router.register("/plans/pay") { ru.cleardocs.lkweb.pages.PlanConnectPage() }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerComponentStyle(ru.cleardocs.lkweb.HeadlineTextStyle)
        ctx.theme.registerComponentStyle(ru.cleardocs.lkweb.SubheadlineTextStyle)
        ctx.theme.registerComponentStyle(ru.cleardocs.lkweb.components.layouts.MarkdownStyle)
        ctx.theme.registerComponentStyle(ru.cleardocs.lkweb.components.layouts.PageContentStyle)
        ctx.theme.registerComponentStyle(ru.cleardocs.lkweb.components.sections.FooterStyle)
        ctx.theme.registerComponentStyle(ru.cleardocs.lkweb.components.sections.NavHeaderStyle)
        ctx.theme.registerComponentStyle(ru.cleardocs.lkweb.components.sections.NavLinkStyle)
        ctx.theme.registerComponentStyle(ru.cleardocs.lkweb.components.sections.DrawerBackdropStyle)
        ctx.theme.registerComponentStyle(ru.cleardocs.lkweb.components.sections.DrawerPanelStyle)
        ctx.theme.registerComponentStyle(ru.cleardocs.lkweb.components.sections.ClearDocsLogoStyle)
        ctx.theme.registerComponentStyle(ru.cleardocs.lkweb.pages.AuthFormWidthStyle)
        ctx.theme.registerComponentVariants(ru.cleardocs.lkweb.CircleButtonVariant)
        ctx.theme.registerComponentVariants(ru.cleardocs.lkweb.UncoloredButtonVariant)
        ctx.theme.registerComponentVariants(ru.cleardocs.lkweb.AuthTabActiveVariant)
        ctx.theme.registerComponentVariants(ru.cleardocs.lkweb.AuthTabInactiveVariant)
        ctx.theme.registerComponentVariants(ru.cleardocs.lkweb.AuthPrimaryButtonVariant)
        ctx.theme.registerComponentVariants(ru.cleardocs.lkweb.AuthGoogleButtonVariant)
        ctx.theme.registerComponentVariants(ru.cleardocs.lkweb.AuthToggleButtonVariant)
        ctx.theme.registerComponentVariants(ru.cleardocs.lkweb.ActionButtonVariant)
        ru.cleardocs.lkweb.initColorMode(ctx)
        ru.cleardocs.lkweb.initSiteStyles(ctx)
        ru.cleardocs.lkweb.initTheme(ctx)
        ru.cleardocs.lkweb.components.sections.registerProfileMenuCardStyle(ctx)
        ru.cleardocs.lkweb.components.sections.registerConfirmDialogStyles(ctx)
    }

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    router.navigateTo(window.location.href.removePrefix(window.location.origin),
            UpdateHistoryMode.REPLACE)

    val root = document.getElementById("root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "root") {
        CompositionLocalProvider(AppGlobalsLocal provides mapOf("title" to "ClearDocs")) {
            ru.cleardocs.lkweb.AppEntry {
                router.renderActivePage { renderWithDeferred { it() } }
            }
        }
    }
}
